import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

import { useDispatch, useSelector } from "react-redux";
import { getDateAssigmentments } from "store/slice/linesGeneration";
import { CHINES_STUDIO_ID, SHUFFLERS_STUDIO_ID } from "constats";
import { parse } from "date-fns";
import { de } from "date-fns/locale";

dayjs.extend(utc);

const generateTimeSlots = (shiftType) => {
  const startHour = shiftType === "1" ? 9 : 21;

  const slots = [];
  const start = new Date();
  start.setHours(startHour, 0, 0, 0);

  // example: 12 hours shift (change if needed)
  const TOTAL_MINUTES = 12 * 60; 
  const STEP = 20;

  for (let m = 0; m < TOTAL_MINUTES; m += STEP) {
    const t = new Date(start);
    t.setMinutes(start.getMinutes() + m);

    const hh = String(t.getHours()).padStart(2, "0");
    const mm = String(t.getMinutes()).padStart(2, "0");

    slots.push(`${hh}:${mm}`);
  }
  
  return slots;
};
const checkIsPastDate = (date) => {
  const shiftDate = new Date(date);
  const now = new Date().setHours(0, 0, 0, 0);

  return (now - shiftDate >= 0);
}

const Details = ({ shiftConf }) => {
  const dispatch = useDispatch();
  const store = useSelector((state) => state.linesGeneration);
  const usersStore = useSelector(store => store.user)

  const dealers = store.workingDealers || [];
  const assignments = store.assignments || [];
  const account = usersStore.user;

  const [shiftType, setShiftType] = useState("1");
  const [shiftDay, setShiftDay] = useState(null);

  useEffect(() => {
    if (!shiftConf) return;

    const start = dayjs(shiftConf.startDate).startOf("day");
    const end = dayjs(shiftConf.endDate).startOf("day");
    const today = dayjs().startOf("day");

    if (today.isBefore(start) || today.isAfter(end)) {

      setShiftDay(start);
    } else {
      setShiftDay(today);
    }
  }, [shiftConf]);

  const start = useMemo(
    () => (shiftConf ? dayjs(shiftConf.startDate).startOf("day") : null),
    [shiftConf]
  );
  const end = useMemo(
    () => (shiftConf ? dayjs(shiftConf.endDate).startOf("day") : null),
    [shiftConf]
  );

  const dateRange = useMemo(
    () => generateTimeSlots(shiftType),
    [shiftType]
  );

  useEffect(() => {
    if (!shiftDay) return;
    dispatch(getDateAssigmentments({ date: shiftDay.format("YYYY-MM-DD") }));
  }, [shiftDay, dispatch]);

  if (!shiftConf || !start || !end) return null;

  const shouldDisableDate = (date) => {
    const d = date.startOf("day");
    return d.isBefore(start) || d.isAfter(end);
  };

  const dealerCellStyle = { width: "250px", fontWeight: "bold", fontSize: 16, paddingLeft: 10 };

  // (optional) group dealers — your logic stays
  const chinesDealers = [];
  const shufflers = [];
  const otherDealers = [];

  dealers.forEach((d) => {
    if (d.shift !== parseInt(shiftType)) return;
    const studioId = d.studio?.id;
    if (studioId === CHINES_STUDIO_ID) chinesDealers.push(d);
    else if (studioId === SHUFFLERS_STUDIO_ID) shufflers.push(d);
    else otherDealers.push(d);
  });
  let prevDate;
  const renderDealerRow = (data, key, idx) => {
    const dealer = data.dealer;
     if (!dealer) return;
          const isCurrentDealer = dealer?.id == account?.id;
  
          dealerCellStyle['background'] = 'rgba(240, 242, 245, 1)';
          dealerCellStyle['color'] = '';
          if (isCurrentDealer == true) {
              dealerCellStyle['background'] = 'green';
              dealerCellStyle['color'] = '#fff';
          }
          
          return ((
              <tr key={key} style={{lineHeight: 1}}>
                  <td style={{width: "60px", textAlign: "center", fontSize: 16, background: "rgba(240, 242, 245, 1)"}}>
                      {idx}
                  </td>
                  <td style={{...dealerCellStyle}}>
                      {dealer.name}
                  </td>
                  <td style={{background: "rgba(240, 242, 245, 1)"}}>
                      <ul style={{ margin: 0, listStyle: "none", fontSize: "15px", marginLeft: 10 }}>
                          {data.studio.name}
                      </ul>
                  </td>
                  {
                  
                  dateRange.map((timeStr, idx) => {
                    const [hh, mm] = timeStr.split(":").map(Number);

                    const assignment = assignments.find(a => {
                      const t = dayjs.utc(a.startAt);
              
                      return (
                        t.hour() === hh &&
                        t.minute() === mm &&
                        a.dealerId === dealer.id
                      );
                    }
                    );
                    prevDate = timeStr;
                    return <td key={idx}>{assignment?.game?.name ?? ""}</td>;
                  })}
              </tr>
          ))
      };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {/* Title */}
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
        Lines Generation Details
      </Typography>

      {/* Subtitle */}
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
        Allowed range: {start.format("DD MMM YYYY")} — {end.format("DD MMM YYYY")}
      </Typography>

      <Divider sx={{ mb: 2, opacity: 0.4 }} />

      {/* Calendar + Controls row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap", // responsive
        }}
      >
        {/* Calendar */}
        <Box sx={{ minWidth: 320 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={shiftDay}
              onChange={(newValue) => newValue && setShiftDay(newValue)}
              shouldDisableDate={shouldDisableDate}
              minDate={start}
              maxDate={end}
              sx={{
                "&.MuiDateCalendar-root": { p: 0 },
                "& .MuiPickersCalendarHeader-root": { pl: 0, pr: 0, mb: 1 },
                "& .MuiDayCalendar-header": { ml: 0, mr: 0 },
                "& .MuiDayCalendar-monthContainer": { ml: 0 },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Controls */}
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 0.75 }}>
            Shift type
          </Typography>

          <ToggleButtonGroup
            value={shiftType}
            exclusive
            onChange={(_, v) => v && setShiftType(v)}
            size="small"
            sx={{
              borderRadius: 999,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                px: 1.5,
                py: 0.6,
                borderRadius: 0,
                fontWeight: 600,
                opacity: 0.85,
              },
              "& .MuiToggleButton-root.Mui-selected": {
                opacity: 1,
              },
            }}
          >
            <ToggleButton value="1">Morning</ToggleButton>
            <ToggleButton value="2">Night</ToggleButton>
          </ToggleButtonGroup>

          {/* Optional: small info card style without white background */}
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.08)",
              maxWidth: 260,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Quick stats
            </Typography>
            {/* <Typography variant="caption" sx={{ opacity: 0.75, display: "block" }}>
              Chines: {chinesDealers.length}
            </Typography> */}
            <Typography variant="caption" sx={{ opacity: 0.75, display: "block" }}>
              Shufflers: {shufflers.length}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75, display: "block" }}>
              Other: {otherDealers.length}
            </Typography>
          </Box>
        </Box>
      </Box>

      <div style={{ overflow: "auto", maxHeight: "900px", display: "inline-block", border: "1px solid #ccc", position: "relative", zIndex: 9999 }}>
                <table border="1" cellPadding="5" cellSpacing="0" style={{ borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 2, fontSize: 16 }}>Id</th>
                            <th style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 2 , fontSize: 16}}>Dealer</th>
                            <th style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 2, width: 150, fontSize: 16 }}>Studios</th>
                            {dateRange.map(date => {
                                const isPastDate = checkIsPastDate(date);

                                return (
                                    <th
                                        key={date}
                                        style={{
                                            writingMode: "vertical-rl",
                                            whiteSpace: "nowrap",
                                            position: 'sticky',
                                            top: 0,
                                            color: "black",
                                            zIndex: 2,
                                            paddingTop: 10,
                                            paddingBottom: 10,
                                            minWidth: 40,
                                            fontSize: 16
                                        }}
                                    >
                                        {date}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                      
                        {/* {chinesDealers.map((dealer, idx) => {
                          return renderDealerRow(dealer, `ch-${idx}`, ++idx);
                        })} */}
         
                        <tr style={{lineHeight: 1}}>
                            <td></td>
                            <td></td>
                            <td></td>
                            {/* {dateRange.map((date, idx) => {
                                    return (
                                        <td
                                            key={idx}
                                            style={{textAlign: "center"}}
                                        >
                                            {chinesCounts[date]? chinesCounts[date][1]: ""}
                                        </td>
                                    );
                            })} */}
                        </tr>
                        <tr style={{lineHeight: 1}}>
                            <td></td>
                            <td></td>
                            <td></td>
                            {/* {dateRange.map((date, idx) => {
                                    return (
                                        <td
                                            key={idx}
                                            style={{textAlign: "center"}}
                                            
                                        >
                                            {chinesCounts[date] ? chinesCounts[date][2]: ""}
                                        </td>
                                    );
                            })} */}
                        </tr>
                        <tr>
                            <td colSpan={1+ 2} style={{ textAlign: "center", background: "#eee", fontWeight: "bold" }}>
                                Shufflers
                            </td>
                        </tr>
                        {
                            shufflers.map((dealer, idx) => {
                                return renderDealerRow(dealer, `ot-${idx}`, ++idx);
                            })
                        }
                        <tr style={{lineHeight: 1}}>
                            <td></td>
                            <td></td>
                            <td></td>
                            {/* {dateRange.map((date, idx) => {
                                    return (
                                        <td
                                            key={idx}
                                            style={{textAlign: "center"}}
                                        >
                                            {shufflersCounts[date]? shufflersCounts[date][1]: "0"}
                                        </td>
                                    );
                            })} */}
                        </tr>
                        <tr style={{lineHeight: 1}}>
                            <td></td>
                            <td></td>
                            <td></td>
                            {/* {dateRange.map((date, idx) => {
                                    return (
                                        <td
                                            key={idx}
                                            style={{textAlign: "center"}}
                                            
                                        >
                                            {shufflersCounts[date] ? shufflersCounts[date][2]: ""}
                                        </td>
                                    );
                            })} */}
                        </tr>

                        <tr>
                            <td colSpan={1 + 2} style={{ textAlign: "center", background: "#eee", fontWeight: "bold" }}>
                                New Studio and Armenian Studios Dealers
                            </td>
                        </tr>

                        {
                            otherDealers.map((dealer, idx) => {
                                return renderDealerRow(dealer, `ot-${idx}`, ++idx);
                            })
                        }
                    </tbody>
                </table>
            </div>
    </Box>
  );
};

export default Details;